import { AppStoreBadges } from '@/shared/ui/components'
import { Button } from '@/shared/ui/primitives'
import { AuthLayout } from './auth-layout'
import { formatOrgList } from './format-org-list'

export type NoCommandCenterAccessProps = {
  identity?: string
  activeOrgNames: string[]
  invitationOrgNames: string[]
  onUseDifferentAccount: () => void
  onRegister: () => void
}

function OrgNames({ names }: { names: string[] }) {
  return <span className="font-semibold text-foreground">{formatOrgList(names)}</span>
}

export function NoCommandCenterAccess({
  identity,
  activeOrgNames,
  invitationOrgNames,
  onUseDifferentAccount,
  onRegister,
}: NoCommandCenterAccessProps) {
  const hasActiveOrgs = activeOrgNames.length > 0
  const hasInvitations = invitationOrgNames.length > 0
  const belongsToOrg = hasActiveOrgs || hasInvitations
  const subtitle = identity ? `Signed in as ${identity}` : undefined

  return (
    <AuthLayout
      title={
        belongsToOrg
          ? 'Command Center is for managers and owners'
          : "This account isn't linked to a club or outfitter"
      }
      subtitle={subtitle}
    >
      <div data-testid="no-command-center-access" className="flex flex-col gap-6">
        <div className="flex flex-col gap-3 text-sm text-muted-foreground">
          {hasActiveOrgs ? (
            <p>
              You&apos;re part of <OrgNames names={activeOrgNames} />. Command Center is only
              available to club managers and outfitter owners. Everything you need — your hunts,
              bookings, invitations, and hunt log — is in the BlindBook app.
            </p>
          ) : null}
          {!hasActiveOrgs && hasInvitations ? (
            <p>
              Command Center is only available to club managers and outfitter owners. Members use
              the BlindBook app for their hunts, bookings, and hunt log.
            </p>
          ) : null}
          {hasInvitations ? (
            <p>
              You have an invitation from <OrgNames names={invitationOrgNames} />. Open the
              BlindBook app to accept it.
            </p>
          ) : null}
          {!belongsToOrg ? (
            <>
              <p>
                Command Center is where club managers and outfitter owners run their operation. As
                a hunter, you&apos;ll find your hunt log, maps, and weather in the BlindBook app.
              </p>
              <p>
                Expecting access? Ask your club manager or outfitter to invite{' '}
                {identity ? <span className="font-semibold text-foreground">{identity}</span> : 'this account'}, or
                sign in with a different account.
              </p>
            </>
          ) : null}
        </div>

        <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card p-4">
          <p className="text-sm font-semibold text-foreground">Get the BlindBook app</p>
          <AppStoreBadges />
          <p className="text-center text-xs text-muted-foreground">
            Sign in to the app with the same email or phone number.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {!belongsToOrg ? (
            <Button type="button" variant="outline" onClick={onRegister} className="w-full">
              Register a club or outfitter
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={onUseDifferentAccount} className="w-full">
            Use a different account
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}
